import { useSelector } from "react-redux";
//#region types
import { CurrencyEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { AppState } from "store";
//#endregion
//#region components
//#endregion
import { DateConstants } from "utils/constants";
import ExportHelper from "utils/helpers/exportHelper";
import { LocalizationHelperInstance } from "utils/helpers";
import { Amount } from "models/utilTypes/currencyType";

type ExportFormat = "xlsx" | "csv";

export default function useCompanyBooksExport() {
  const transactionsTitle = `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Transactions)}`;
  const allDimensionTypes = useSelector((store: AppState) => store.common.dimensions.allDimensionTypes) ?? [];
  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");

  const fileName = `${transactionsTitle} - ${companyName}`;

  const formatDebitCredit = (
    amount: number,
    currencyCode: CurrencyEnums.CurrencyCode = CurrencyEnums.CurrencyCode.SEK
  ): [string, string] => {
    const currencyAmount = new Amount(currencyCode, Math.abs(Number(amount) || 0));
    const currencyFormattedAmount = `${currencyAmount.currency} ${currencyAmount.amount}`;
    const debit = amount >= 0 ? currencyFormattedAmount : "";
    const credit = amount < 0 ? currencyFormattedAmount : "";
    return [debit, credit];
  };

  const buildExportAoa = (items: any[]): any[][] => {
    const fixedColumns: { header: string; key: string }[] = [
      {
        header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.SeriesExportHeader),
        key: "voucherSeriesShortName",
      },
      {
        header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.VoucherNumber),
        key: "voucherNumber",
      },
      { header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Date), key: "bookingDate" },
      {
        header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.RecipientOrSender),
        key: "correspondentName",
      },
      {
        header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.TransactionText),
        key: "voucherText",
      },
      { header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Account), key: "accountNumber" },
      {
        header: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AccountName),
        key: "accountName",
      },
    ];

    const dimensionTypesById = ExportHelper.getDimensionTypesById(allDimensionTypes);
    const allDimensionTypeNames = ExportHelper.getAllDimensionTypeNamesHeaders(items, dimensionTypesById);

    const headers = [
      ...fixedColumns.map((c) => c.header),
      ...allDimensionTypeNames,
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Debit),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Credit),
    ];

    const rows = (items as any[]).map((item) => {
      const fixedValues = fixedColumns.map(({ key }) =>
        key === "bookingDate" ? LocalizationHelperInstance.getFormattedDate(item?.[key]) : (item?.[key] ?? "")
      );
      const dimensionValues = ExportHelper.buildDimensionValues(item, allDimensionTypeNames, dimensionTypesById);
      const amount: number = typeof item?.amount === "number" ? item.amount : Number(item?.amount ?? 0);
      const [debit, credit] = formatDebitCredit(amount);
      return [...fixedValues, ...dimensionValues, debit, credit];
    });

    return [headers, ...rows];
  };

  const exportReportWithFormat = async (format: ExportFormat, items: any[]) => {
    const aoa = buildExportAoa(items);
    const timestamp = LocalizationHelperInstance.getFormattedDate(new Date(), DateConstants.DateTimeConfig);
    ExportHelper.exportReport(aoa, `${fileName}_${timestamp}.${format}`, transactionsTitle);
  };

  const onExportReportCSV = async (items: any[]) => {
    await exportReportWithFormat("csv", items);
  };

  const onExportReportExcel = async (items: any[]) => {
    await exportReportWithFormat("xlsx", items);
  };

  return {
    onExportReportCSV,
    onExportReportExcel,
  };
}
